<template>
  <div class="flsa-jurisdiction-row w-100">
    <div
      v-if="showSingleSummary"
      class="flsa-jurisdiction-row-summary py-6"
    >
      <FlsaSubmissionTimelineItem
        v-if="latestSubmission"
        :submission="latestSubmission"
        is-first
        is-last
        show-jurisdiction-pill
        @click="handleSummaryClick"
      />
    </div>

    <div
      v-else-if="showMultipleSummary"
      class="flsa-jurisdiction-row-summary px-8 py-6"
      @click="handleSummaryClick"
    >
      <div class="d-flex flex-column ga-1">
        <StatusPill
          :status-key="jurisdictionStatusKey(jurisdiction)"
          :label="jurisdictionLabel(jurisdiction)"
        />

        <div class="text-h2 text-textDarkest">
          {{ formatFlsaSubmissionDate(latestSubmission?.created_at) }}
        </div>

        <div class="d-flex align-center ga-1 flex-wrap">
          <span class="text-body-2 text-textDark font-weight-medium">
            {{ latestSubmission?.is_exempt ? 'Exempt' : 'Non-exempt' }}
          </span>
          <template v-if="latestSubmission?.created_by?.name">
            <span class="text-body-2 text-textLight">-</span>
            <span class="text-body-2 text-textLight">{{ latestSubmission.created_by.name }}</span>
          </template>
        </div>

        <button
          type="button"
          class="flsa-jurisdiction-row-view-more text-body-2 text-left mt-1"
          @click.stop="handleViewMoreClick"
        >
          View more submissions
        </button>
      </div>
    </div>

    <FlsaSubmissionTimeline
      v-else-if="isExpanded"
      :submissions="submissions"
      show-jurisdiction-pill
      @submission-click="handleTimelineClick"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import FlsaSubmissionTimeline from '@/components/jobProfiles/editor/FlsaSubmissionTimeline.vue';
import FlsaSubmissionTimelineItem from '@/components/jobProfiles/editor/FlsaSubmissionTimelineItem.vue';
import StatusPill from '@/components/shared/StatusPill.vue';
import {
  formatFlsaSubmissionDate,
  jurisdictionLabel,
  jurisdictionStatusKey,
  sortSubmissionsByDateDesc,
} from '@/helpers/flsa';

const props = defineProps({
  jurisdiction: {
    type: String,
    required: true,
  },
  submissions: {
    type: Array,
    default: () => [],
  },
  defaultExpanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submission-click', 'latest-click']);

const isExpanded = ref(props.defaultExpanded && props.submissions.length > 1);

const sortedSubmissions = computed(() => sortSubmissionsByDateDesc(props.submissions));

const latestSubmission = computed(() => sortedSubmissions.value[0] ?? null);

const showSingleSummary = computed(() => props.submissions.length === 1);

const showMultipleSummary = computed(() => (
  props.submissions.length > 1 && !isExpanded.value
));

const handleSummaryClick = () => {
  if (!latestSubmission.value) return;

  emit('latest-click', latestSubmission.value);
};

const handleViewMoreClick = () => {
  isExpanded.value = true;
};

const handleTimelineClick = (submission) => {
  emit('submission-click', submission);
};
</script>

<style scoped lang="less">
.flsa-jurisdiction-row-summary {
  background: @bg-default;
  cursor: pointer;

  &:hover {
    background: @bg-canvas;
  }
}

.flsa-jurisdiction-row-view-more {
  color: @link-default;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
